import { useTranslate } from "@tolgee/react";
import { HandCoins, Share2, TicketPercent } from "lucide-react";
import { Button } from "#/components/ui/button";
import { Slider } from "#/components/ui/slider";
import { formatCompactNumber } from "#/lib/formatters";
import { useReferralLoginGate } from "../hooks/use-referral-login-gate";
import { ReferralFlowDiagram } from "./referral-flow-diagram";

const STEPS = [
	{ icon: TicketPercent, labelKey: "referrals.code.create-title" },
	{ icon: Share2, labelKey: "referrals.gate.step-share" },
	{ icon: HandCoins, labelKey: "referrals.gate.step-earn" },
] as const;

/** Logged-out `/referrals` view: flow diagram + steps + earnings simulator. */
export function ReferralLoginGate({
	onLogin,
	loginDisabled,
}: {
	onLogin: () => void;
	loginDisabled: boolean;
}) {
	const { t } = useTranslate();
	const gate = useReferralLoginGate();

	return (
		<div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center gap-8 p-4 py-16">
			<div className="text-center">
				<h1 className="text-title1 text-foreground">
					{gate.maxRounded > 0
						? t("referrals.fee.title", { max: gate.maxRounded })
						: t("referrals.connect-prompt")}
				</h1>
				<p className="mt-2 text-body2 text-muted-foreground">
					{t("referrals.fee.subtitle")}
				</p>
			</div>

			{/* the card in the illustration is the simulator's live result display */}
			<div>
				<ReferralFlowDiagram
					monthlyRebate={gate.rebateRatePct > 0 ? gate.monthlyRebate : null}
					tradeRebate={gate.rebateRatePct > 0 ? gate.sampleTradeRebate : null}
				/>
				{gate.rebateRatePct > 0 && (
					<div className="mt-5">
						<div className="flex items-baseline justify-between gap-4">
							<span className="text-label2-medium text-muted-foreground">
								{t("referrals.gate.sim-volume")}
							</span>
							<span className="text-label1-tabular-bold text-foreground">
								${formatCompactNumber(gate.volume)}
							</span>
						</div>
						<Slider
							className="mt-2"
							value={[gate.sliderValue]}
							onValueChange={(values) => gate.setSliderValue(values[0] ?? 50)}
							min={0}
							max={100}
							step={1}
							aria-label={t("referrals.gate.sim-volume")}
						/>
						<p className="mt-1.5 text-right text-caption1 text-muted-foreground">
							{t("referrals.gate.sim-note", { rate: gate.rateLabel })}
						</p>
					</div>
				)}
			</div>

			<ol className="flex flex-col gap-4">
				{STEPS.map((step) => (
					<li key={step.labelKey} className="flex items-center gap-3">
						<span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
							<step.icon className="size-4.5" strokeWidth={1.75} />
						</span>
						<span className="text-body2 text-foreground">
							{t(step.labelKey)}
						</span>
					</li>
				))}
			</ol>

			<div className="flex justify-center">
				<Button size="lg" onClick={onLogin} disabled={loginDisabled}>
					{t("actions.login")}
				</Button>
			</div>
		</div>
	);
}
